import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { purchaseApi as P, PO_STATUS_STYLE, inr } from "@/lib/purchase";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, PackagePlus, FileText } from "lucide-react";

export default function Purchase() {
  const { can } = useAuth();
  const [stats, setStats] = useState({});
  const [tab, setTab] = useState("orders");
  const canCreate = can("purchase", "create");
  const canApprove = can("purchase", "approve");
  const load = () => P.stats().then(setStats).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-6" data-testid="purchase-page">
      <div><h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Purchase</h2>
        <p className="mt-1 text-sm text-slate-500">Supplier → Purchase Order → Goods Receipt → Raw Material Stock. Stock updates only via an approved-PO GRN.</p></div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi testid="po-stat-open" label="Open POs" value={(stats.Approved ?? 0) + (stats.Sent ?? 0) + (stats["Partially Received"] ?? 0)} />
        <Kpi testid="po-stat-value" label="Open PO value" value={inr(stats.open_value)} accent="text-indigo-600" />
        <Kpi testid="po-stat-completed" label="Completed" value={stats.Completed ?? 0} accent="text-emerald-600" />
        <Kpi testid="po-stat-grn" label="GRNs" value={stats.grn_count ?? 0} accent="text-amber-600" />
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="purchase-tabs">
          <TabsTrigger value="orders" data-testid="tab-orders">Purchase Orders</TabsTrigger>
          <TabsTrigger value="grn" data-testid="tab-grn">Goods Receipt</TabsTrigger>
        </TabsList>
        <TabsContent value="orders" className="mt-4"><OrdersTab canCreate={canCreate} canApprove={canApprove} onChange={load} /></TabsContent>
        <TabsContent value="grn" className="mt-4"><GrnTab canCreate={canCreate} onChange={load} /></TabsContent>
      </Tabs>
    </div>
  );
}

const Kpi = ({ label, value, accent = "text-slate-900", testid }) => (
  <Card className="p-4" data-testid={testid}><p className="text-xs uppercase tracking-wider text-slate-500">{label}</p><p className={`mt-1 font-heading text-xl font-bold ${accent}`}>{value}</p></Card>
);

function OrdersTab({ canCreate, canApprove, onChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const load = () => { setLoading(true); P.listPO().then(setRows).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  return (
    <Card className="p-4">
      <div className="mb-4 flex justify-end">{canCreate && <Button onClick={() => setOpen(true)} data-testid="add-po-btn" className="bg-indigo-600 text-white hover:bg-indigo-700"><Plus className="mr-2 h-4 w-4" />New Purchase Order</Button>}</div>
      {loading ? <Spin /> : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><Th>PO</Th><Th>Supplier</Th><Th>Date</Th><Th right>Total</Th><Th>Status</Th></TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className="cursor-pointer" onClick={() => setDetail(r.id)} data-testid={`po-row-${r.po_no}`}>
                  <TableCell className="font-medium text-slate-800">{r.po_no}</TableCell>
                  <TableCell className="text-slate-700">{r.supplier_name}</TableCell>
                  <TableCell className="text-slate-600">{r.po_date}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-700">{inr(r.totals?.total)}</TableCell>
                  <TableCell><Badge className={`hover:bg-inherit ${PO_STATUS_STYLE[r.status]}`}>{r.status}</Badge></TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={5} className="py-12 text-center text-slate-400" data-testid="po-empty">No purchase orders yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}
      <POFormDialog open={open} onOpenChange={setOpen} onDone={() => { load(); onChange?.(); }} />
      <PODetailDialog pid={detail} onClose={() => setDetail(null)} canApprove={canApprove} onChange={() => { load(); onChange?.(); }} />
    </Card>
  );
}

function POFormDialog({ open, onOpenChange, onDone }) {
  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [f, setF] = useState({ supplier_id: "", po_date: "", expected_date: "", remarks: "" });
  const [items, setItems] = useState([{ material_id: "", qty: "", rate: "", tax_pct: "18" }]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { P.suppliers().then(setSuppliers); P.materials().then(setMaterials); setF({ supplier_id: "", po_date: "", expected_date: "", remarks: "" }); setItems([{ material_id: "", qty: "", rate: "", tax_pct: "18" }]); } }, [open]);
  const upd = (i, k, v) => setItems((p) => p.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const total = items.reduce((s, it) => { const a = (Number(it.qty) || 0) * (Number(it.rate) || 0); return s + a + a * (Number(it.tax_pct) || 0) / 100; }, 0);
  const submit = async () => {
    const lines = items.filter((it) => it.material_id && Number(it.qty) > 0).map((it) => ({ material_id: it.material_id, qty: Number(it.qty), rate: Number(it.rate) || 0, tax_pct: Number(it.tax_pct) || 0 }));
    if (!f.supplier_id || lines.length === 0) { toast.error("Select supplier and add material lines"); return; }
    setBusy(true);
    try { const r = await P.createPO({ ...f, items: lines }); toast.success(`${r.po_no} created`); onOpenChange(false); onDone?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>New Purchase Order</DialogTitle><DialogDescription>Multiple materials allowed. Created as Draft; approve before receiving.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Supplier</Label>
              <Select value={f.supplier_id} onValueChange={(v) => setF({ ...f, supplier_id: v })}>
                <SelectTrigger data-testid="po-supplier-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">PO date</Label><Input type="date" value={f.po_date} onChange={(e) => setF({ ...f, po_date: e.target.value })} data-testid="po-date-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Expected</Label><Input type="date" value={f.expected_date} onChange={(e) => setF({ ...f, expected_date: e.target.value })} data-testid="po-expected-input" /></div>
          </div>
          <div className="space-y-2 rounded-md border border-slate-200 p-2">
            {items.map((it, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`po-item-${i}`}>
                <Select value={it.material_id} onValueChange={(v) => { const m = materials.find((x) => x.id === v); upd(i, "material_id", v); if (m && !it.rate) upd(i, "rate", String(m.default_rate || "")); }}>
                  <SelectTrigger className="flex-1" data-testid={`po-item-material-${i}`}><SelectValue placeholder="Material" /></SelectTrigger>
                  <SelectContent>{materials.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} ({m.unit})</SelectItem>)}</SelectContent>
                </Select>
                <Input type="number" placeholder="Qty" value={it.qty} onChange={(e) => upd(i, "qty", e.target.value)} className="w-20" data-testid={`po-item-qty-${i}`} />
                <Input type="number" placeholder="Rate" value={it.rate} onChange={(e) => upd(i, "rate", e.target.value)} className="w-20" data-testid={`po-item-rate-${i}`} />
                <Input type="number" placeholder="Tax%" value={it.tax_pct} onChange={(e) => upd(i, "tax_pct", e.target.value)} className="w-16" data-testid={`po-item-tax-${i}`} />
                {items.length > 1 && <button onClick={() => setItems((p) => p.filter((_, idx) => idx !== i))} className="text-rose-600"><Trash2 className="h-4 w-4" /></button>}
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => setItems((p) => [...p, { material_id: "", qty: "", rate: "", tax_pct: "18" }])} data-testid="po-add-line-btn"><Plus className="mr-1 h-3.5 w-3.5" />Add line</Button>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Remarks</Label><Input value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} data-testid="po-remarks-input" /></div>
          <p className="text-right text-sm font-medium text-slate-700">Total (incl tax): {inr(total)}</p>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={submit} disabled={busy} data-testid="submit-po-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create PO"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PODetailDialog({ pid, onClose, canApprove, onChange }) {
  const [po, setPo] = useState(null);
  useEffect(() => { if (pid) P.getPO(pid).then(setPo); else setPo(null); }, [pid]);
  const act = async (status) => { try { await P.setPOStatus(pid, status); toast.success(`Marked ${status}`); const r = await P.getPO(pid); setPo(r); onChange?.(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };
  if (!pid) return null;
  return (
    <Dialog open={!!pid} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        {!po ? <Spin /> : (
          <>
            <DialogHeader><DialogTitle className="flex items-center gap-2">{po.po_no}<Badge className={`hover:bg-inherit ${PO_STATUS_STYLE[po.status]}`} data-testid="po-detail-status">{po.status}</Badge></DialogTitle><DialogDescription>{po.supplier_name} · {po.po_date}</DialogDescription></DialogHeader>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="bg-slate-50"><Th>Material</Th><Th right>Qty</Th><Th right>Received</Th><Th right>Rate</Th><Th right>Total</Th></TableRow></TableHeader>
                <TableBody>
                  {po.items.map((it) => (
                    <TableRow key={it.id}><TableCell className="text-slate-800">{it.material_name}</TableCell><TableCell className="text-right tabular-nums">{it.qty} {it.unit}</TableCell><TableCell className="text-right tabular-nums text-emerald-600">{it.received_qty}</TableCell><TableCell className="text-right tabular-nums">{inr(it.rate)}</TableCell><TableCell className="text-right tabular-nums">{inr(it.total)}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="text-right text-sm font-semibold text-slate-800">Total: {inr(po.totals?.total)}</p>
            {(po.grns || []).length > 0 && <p className="text-xs text-slate-500">GRNs: {po.grns.map((g) => g.grn_no).join(", ")}</p>}
            {canApprove && (
              <DialogFooter>
                {po.status === "Draft" && <Button onClick={() => act("Approved")} data-testid="po-approve-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">Approve</Button>}
                {po.status === "Approved" && <Button onClick={() => act("Sent")} data-testid="po-send-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">Mark Sent</Button>}
                {["Draft", "Approved", "Sent", "Partially Received"].includes(po.status) && <Button variant="outline" className="text-rose-600" onClick={() => act("Cancelled")} data-testid="po-cancel-btn">Cancel</Button>}
              </DialogFooter>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function GrnTab({ canCreate, onChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const load = () => { setLoading(true); P.listGRN().then(setRows).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  return (
    <Card className="p-4">
      <div className="mb-4 flex justify-end">{canCreate && <Button onClick={() => setOpen(true)} data-testid="add-grn-btn" className="bg-indigo-600 text-white hover:bg-indigo-700"><PackagePlus className="mr-2 h-4 w-4" />Receive (GRN)</Button>}</div>
      {loading ? <Spin /> : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><Th>GRN</Th><Th>PO</Th><Th>Supplier</Th><Th>Warehouse</Th><Th right>Lines</Th></TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`grn-row-${r.grn_no}`}>
                  <TableCell className="font-medium text-slate-800">{r.grn_no}<span className="block text-xs text-slate-400">{r.received_date}</span></TableCell>
                  <TableCell className="text-slate-600">{r.po_no}</TableCell>
                  <TableCell className="text-slate-700">{r.supplier_name}</TableCell>
                  <TableCell className="text-slate-600">{r.warehouse}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-600">{r.items?.length}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={5} className="py-12 text-center text-slate-400" data-testid="grn-empty">No goods receipts yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}
      <GrnDialog open={open} onOpenChange={setOpen} onDone={() => { load(); onChange?.(); }} />
    </Card>
  );
}

function GrnDialog({ open, onOpenChange, onDone }) {
  const [pos, setPos] = useState([]);
  const [poId, setPoId] = useState("");
  const [po, setPo] = useState(null);
  const [recv, setRecv] = useState({});
  const [warehouse, setWarehouse] = useState("Main Store");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { P.listPO({ status: "Approved" }).then((a) => P.listPO({ status: "Sent" }).then((b) => P.listPO({ status: "Partially Received" }).then((c) => setPos([...a, ...b, ...c])))); setPoId(""); setPo(null); setRecv({}); } }, [open]);
  useEffect(() => { if (poId) P.getPO(poId).then((p) => { setPo(p); const init = {}; p.items.forEach((it) => { init[it.id] = ""; }); setRecv(init); }); else setPo(null); }, [poId]);
  const submit = async () => {
    const items = Object.entries(recv).filter(([, v]) => Number(v) > 0).map(([po_item_id, v]) => ({ po_item_id, qty_received: Number(v) }));
    if (!poId || items.length === 0) { toast.error("Select PO and enter received quantities"); return; }
    setBusy(true);
    try { const r = await P.createGRN({ po_id: poId, items, warehouse }); toast.success(`${r.grn_no} received — stock updated`); onOpenChange(false); onDone?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-indigo-600" />Goods Receipt</DialogTitle><DialogDescription>Receive against an approved PO. Updates Raw Material Stock. Partial receipts allowed.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">Purchase Order</Label>
            <Select value={poId} onValueChange={setPoId}>
              <SelectTrigger data-testid="grn-po-select"><SelectValue placeholder="Select approved/sent PO" /></SelectTrigger>
              <SelectContent>{pos.map((p) => <SelectItem key={p.id} value={p.id}>{p.po_no} · {p.supplier_name}</SelectItem>)}</SelectContent>
            </Select>
            {pos.length === 0 && <p className="text-xs text-amber-600">No approved POs available to receive.</p>}
          </div>
          {po && (
            <div className="space-y-2 rounded-md border border-slate-200 p-2">
              {po.items.map((it) => { const pending = it.qty - (it.received_qty || 0); return (
                <div key={it.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`grn-line-${it.id}`}>
                  <span className="text-slate-700">{it.material_name} <span className="text-xs text-slate-400">(pending {pending} {it.unit})</span></span>
                  <Input type="number" className="h-8 w-24" placeholder="0" value={recv[it.id] ?? ""} onChange={(e) => setRecv({ ...recv, [it.id]: e.target.value })} data-testid={`grn-recv-${it.id}`} />
                </div>
              ); })}
            </div>
          )}
          <div className="space-y-1.5"><Label className="text-xs">Warehouse</Label><Input value={warehouse} onChange={(e) => setWarehouse(e.target.value)} data-testid="grn-warehouse-input" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={submit} disabled={busy || !poId} data-testid="submit-grn-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Receive"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Th = ({ children, right }) => <TableHead className={`text-xs uppercase text-slate-500 ${right ? "text-right" : ""}`}>{children}</TableHead>;
const Spin = () => <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;
